import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const colorStyle = (color) =>
  Array.isArray(color)
    ? {
        backgroundImage: `linear-gradient(90deg, ${color[0]}, ${color[1]})`,
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
      }
    : { color: color || undefined };

const ColorDropdown = ({
  options,
  selectedValue,
  onSelect,
  disabled = false,
  placeholder = "Select",
  "data-testid": dataTestId = "color-dropdown",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selectedOption = options.find((option) => option.value === selectedValue);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const viewportMargin = 8;
    const preferredMaxHeight = 384;
    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
      const spaceAbove = rect.top - viewportMargin;
      const openUpward = spaceBelow < preferredMaxHeight && spaceAbove > spaceBelow;
      const maxHeight = Math.min(preferredMaxHeight, Math.max(openUpward ? spaceAbove : spaceBelow, 120));
      setMenuPosition({
        left: rect.left,
        width: rect.width,
        maxHeight,
        top: openUpward ? undefined : rect.bottom + viewportMargin,
        bottom: openUpward ? window.innerHeight - rect.top + viewportMargin : undefined,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full ${disabled ? "pointer-events-none opacity-40" : ""}`}
      data-testid={dataTestId}
    >
      <button
        type="button"
        ref={triggerRef}
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        data-testid={`${dataTestId}-trigger`}
        className="w-full flex items-center justify-between gap-2 py-3 px-4 rounded-md border border-[#CADBD5] dark:border-darkThemeBorder bg-white dark:bg-darkThemeDropdownBg"
      >
        <span
          className="text-base font-bold tracking-[-0.32px] uppercase"
          style={colorStyle(selectedOption?.color)}
        >
          {selectedOption?.label || placeholder}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className={`shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        >
          <path
            d="M19.92 8.9502L13.4 15.4702C12.63 16.2402 11.37 16.2402 10.6 15.4702L4.08002 8.9502"
            stroke="#AFC3BC"
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            data-testid={`${dataTestId}-menu`}
            style={{
              top: menuPosition.top,
              bottom: menuPosition.bottom,
              left: menuPosition.left,
              width: menuPosition.width,
              maxHeight: menuPosition.maxHeight,
            }}
            className="fixed rounded-md shadow-appShadow dark:shadow-darkThemeDropdownMenuShadow z-[1000] overflow-y-auto"
          >
            <ul className="bg-white dark:bg-darkThemeDropdownBg rounded-lg w-full">
              {options.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    data-testid={`${dataTestId}-option-${option.value.toLowerCase()}`}
                    onClick={() => handleSelect(option.value)}
                    className={`w-full text-left px-4 py-3 border-b border-grey last:border-b-0 ${
                      selectedValue === option.value ? "bg-[#FFF1C6] dark:bg-darkThemeDropdownItemBg" : ""
                    }`}
                  >
                    <span
                      className="text-base font-bold tracking-[-0.32px] uppercase"
                      style={colorStyle(option.color)}
                    >
                      {option.label}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ColorDropdown;
